import React, { useContext, useMemo } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

import MasterLayout from '../AppLayout/MasterLayout';
import { routes } from '../../constants';
import { AsyncActionStatus } from '../../actions/common.actionTypes';
import OwnCollectionOverviewEmptyState from '../OwnCollectionOverview/OwnCollectionOverviewEmptyState';
import { CollectionProperties } from '../../types/AppContent';
import Breadcrumbs from '../Breadcrumbs';
import CuratedCollectionsList from './CuratedColletionsList';
import NotificationAlert from '../NotificationAlert/NotificationAlert';
import { spaces } from '../../assets/styles/themes/tokens';
import { NotificationContext } from '../../state/NotificationContextProvider';

interface CuratedCollectionsProps {
  curatedCollections: CollectionProperties[];
  collectionsStatus: AsyncActionStatus;
}

const CuratedCollections = ({ curatedCollections, collectionsStatus }: CuratedCollectionsProps) => {
  const { t } = useTranslation();
  const { notification, hideNotification } = useContext(NotificationContext);

  const links = [
    {
      title: t('Followed Collections'),
      url: `/${routes.followed.root}`,
    },
    {
      title: t('Curated Collections'),
    }
  ];

  const memoizedNotification = useMemo(() => {
    if (!notification) return null;

    return (
      <Box sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
        <Box sx={{ position: 'relative' }}>
          <NotificationAlert
            message={notification.message}
            severity={notification.severity}
            onClose={hideNotification}
            alertSx={{
              position: 'absolute',
              width: `calc(100% - ${spaces.md} * 2)`,
              top: 0,
              left: spaces.md,
            }}
          />
        </Box>
      </Box>
    );
  }, [notification, hideNotification]);

  return (
    <Box data-testid='curated-collections-content'>
      <MasterLayout isNewMerklistePage={true} customTopBarContent={<Breadcrumbs links={links} />}>
        {memoizedNotification}
        {
          (collectionsStatus === AsyncActionStatus.LOADING || curatedCollections?.length === 0) ?
            <OwnCollectionOverviewEmptyState /> :
            <CuratedCollectionsList collections={curatedCollections} />
        }
      </MasterLayout>
    </Box>
  );
};

export default CuratedCollections;
